import { graphql } from "babel-plugin-relay/macro";
import { useTitle } from "../../lib/title";
import { useSearchParams } from "react-router-dom";
import { BadRequestErrorPage } from "../ErrorPage";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";
import { selfServicePageQuery } from "./__generated__/selfServicePageQuery.graphql";
import { useEffect } from "react";
import styles from "./selfServiceSettings.module.scss";
import { SelfServiceStatus } from "./selfServiceStatus";
import { SelfServiceSettingsForm } from "./selfServiceSettings";
import { Tab, Tabs } from "react-bootstrap";
import { SelfServiceUploaderPage } from "./selfServiceUploader";
import { SelfServiceUploadHistory } from "./selfServiceUploadHistory";

export const SelfServicePageQuery = graphql`
    query selfServicePageQuery($ujId: Int!) {
        currentUser {
            isAdmin
        }
        userJourney(ujId: $ujId) {
            ...selfServiceSettings_mainSettingsFragment
            ...selfServiceStatus_statusFragment
            isSelfService
        }
    }
`;

export function SelfServiceSettingsPage() {
    useTitle("JourneyScribe");

    const [searchParams] = useSearchParams();
    const ujID = searchParams.get("uj");

    if (!ujID) {
        // overrides of ujID already check that it is a valid number
        return <BadRequestErrorPage />;
    }

    return <SelfServiceSettingsQueryLoader ujID={parseInt(ujID)} />;
}

function SelfServiceSettingsQueryLoader({ ujID }: { ujID: number }) {
    const [selfServicePageQueryReference, selfServicePageQueryLoader] =
        useQueryLoader<selfServicePageQuery>(SelfServicePageQuery);

    useEffect(() => {
        selfServicePageQueryLoader({ ujId: ujID });
    }, [selfServicePageQueryLoader, ujID]);

    return (
        <>
            {selfServicePageQueryReference && (
                <SelfServiceSettingsContent
                    selfServiceSettingsPageReference={
                        selfServicePageQueryReference
                    }
                    ujID={ujID}
                />
            )}
        </>
    );
}

function SelfServiceSettingsContent({
    selfServiceSettingsPageReference,
    ujID,
}: {
    selfServiceSettingsPageReference: PreloadedQuery<selfServicePageQuery>;
    ujID: number;
}) {
    const data = usePreloadedQuery<selfServicePageQuery>(
        SelfServicePageQuery,
        selfServiceSettingsPageReference,
    );

    useEffect(() => {
        if (!data.userJourney.isSelfService) {
            window.location.assign(
                "/portal2/userJourney/home" + window.location.search,
            );
        }
    }, [data.userJourney.isSelfService]);

    return (
        <>
            {data.userJourney.isSelfService && (
                <div>
                    <div className={styles.selfServiceSettingsContainer}>
                        <h1>JourneyScribe</h1>

                        <Tabs transition={false}>
                            <Tab
                                className={styles.statusSection}
                                eventKey={"settings"}
                                title={"Settings"}
                            >
                                <SelfServiceSettingsForm
                                    userJourney={data.userJourney}
                                    ujID={ujID}
                                    isAdmin={data.currentUser.isAdmin}
                                />
                            </Tab>

                            <Tab
                                className={styles.statusSection}
                                eventKey={"actions"}
                                title={"Actions"}
                            >
                                <SelfServiceStatus
                                    userJourney={data.userJourney}
                                    ujID={ujID}
                                />
                            </Tab>

                            <Tab
                                className={styles.statusSection}
                                eventKey={"uploader"}
                                title={"Uploader"}
                            >
                                <SelfServiceUploaderPage ujID={ujID} />
                            </Tab>

                            <Tab
                                className={styles.statusSection}
                                eventKey={"uploadHistory"}
                                title={"Upload History"}
                            >
                                <SelfServiceUploadHistory ujID={ujID} />
                            </Tab>
                        </Tabs>
                    </div>
                </div>
            )}
        </>
    );
}
